<template>
	<Dialog
		:visible="visible"
		@update:visible="$emit('update:visible', $event)"
		:header="editData ? 'Edit Alternative' : 'Add Alternative'"
		:modal="true"
		:closable="false"
		style="width: 500px"
	>
		<div class="flex flex-col gap-4">
			<div class="flex flex-col gap-1">
				<label class="text-sm font-semibold text-gray-600">Packaging Format</label>
				<Select
					v-model="form.packagingGIId"
					:options="packagingOptions"
					optionLabel="label"
					optionValue="value"
					placeholder="Select Format"
					class="w-full"
				/>
			</div>
			<div class="flex flex-col gap-1">
				<label class="text-sm font-semibold text-gray-600">Distribution</label>
				<Select
					v-model="form.distributionGIId"
					:options="distributionOptions"
					optionLabel="label"
					optionValue="value"
					placeholder="Select Distribution"
					class="w-full"
				/>
			</div>
			<div class="flex flex-col gap-1">
				<label class="text-sm font-semibold text-gray-600">Amount</label>
				<InputNumber v-model="form.amount" :min="1" class="w-full" />
			</div>
			<div class="flex flex-col gap-1">
				<label class="text-sm font-semibold text-gray-600">Scaling Per</label>
				<Select
					v-model="form.scaling"
					:options="scalingOptions"
					optionLabel="label"
					optionValue="value"
					placeholder="Select Scaling"
					class="w-full"
				/>
			</div>
		</div>
		<template #footer>
			<div class="flex justify-end gap-2">
				<Button label="Cancel" severity="secondary" @click="$emit('update:visible', false)" />
				<Button :label="editData ? 'Save' : 'Add'" @click="save" />
			</div>
		</template>
	</Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from "vue"
import Dialog from "primevue/dialog"
import Button from "primevue/button"
import Select from "primevue/select"
import InputNumber from "primevue/inputnumber"

interface SelectOption {
	label: string
	value: string
}

interface AlternativeForm {
	id?: string
	packagingGIId: string | null
	distributionGIId: string | null
	amount: number | null
	scaling: string | null
}

const props = defineProps({
	visible: {
		type: Boolean,
		required: true,
		default: false,
	},
	packagingOptions: {
		type: Array as () => SelectOption[],
		required: false,
		default: () => [],
	},
	distributionOptions: {
		type: Array as () => SelectOption[],
		required: false,
		default: () => [],
	},
	scalingOptions: {
		type: Array as () => SelectOption[],
		required: false,
		default: () => [],
	},
	editData: {
		type: Object as () => AlternativeForm | null,
		required: false,
		default: null,
	},
})

const emit = defineEmits<{
	"update:visible": [value: boolean]
	save: [data: AlternativeForm]
}>()

const form = ref<AlternativeForm>({
	id: undefined,
	packagingGIId: null,
	distributionGIId: null,
	amount: null,
	scaling: null,
})

// Populate form when editData changes (edit mode)
watch(
	() => props.editData,
	(newData) => {
		if (newData) {
			form.value = {
				id: newData.id,
				packagingGIId: newData.packagingGIId,
				distributionGIId: newData.distributionGIId,
				amount: newData.amount,
				scaling: newData.scaling,
			}
		} else {
			form.value = {
				id: undefined,
				packagingGIId: null,
				distributionGIId: null,
				amount: null,
				scaling: null,
			}
		}
	},
	{ immediate: true }
)

// Reset form when dialog is closed
watch(
	() => props.visible,
	(isVisible) => {
		if (!isVisible && !props.editData) {
			form.value = {
				id: undefined,
				packagingGIId: null,
				distributionGIId: null,
				amount: null,
				scaling: null,
			}
		}
	}
)

function save() {
	emit("save", { ...form.value })
	emit("update:visible", false)
}
</script>
